<template>
  <leaflet-map
    ref="fullMap"
    style="height: 80vh;"
    fit-once
    :geolocate="false"
    :show-markers-text="showMarkersText"
    @marker-over="markerOver"
    @marker-out="markerOut"
  >
    <template #controls-topright>
      <div v-if="withTracking && shipping.courier_id" class="mt-2 leaflet-control-layers leaflet-control-layers-expanded">
        <b-form-checkbox v-model="showTracking">
          <b-spinner small v-if="trackingWorking"></b-spinner>
          <translate>Show tracking</translate>
        </b-form-checkbox>
        <b-form-checkbox v-if="showTracking" v-model="showHeathPath" :disabled="trackingWorking">
          <translate>Show heathPath</translate>
        </b-form-checkbox>
        <b-form-checkbox v-if="showTracking" v-model="showMarkersText" :disabled="trackingWorking">
          <translate>Show Markers' Numbers</translate>
        </b-form-checkbox>
        <b-form-checkbox v-if="showTracking" v-model="showList">
          <translate>Show Markers' List</translate>
        </b-form-checkbox>
        <b-form-checkbox v-if="showList" v-model="centerPoints" class="ml-3">
          <translate>Follow Points</translate>
        </b-form-checkbox>
        <b-table id="shipping-tracking-table" v-if="showList" :items="tracking" :fields="trackingHistoryListFields" :sort-by.sync="trackingHistoryListSortBy" :sort-desc.sync="trackingHistoryListSortDesc" @row-hovered="rowHovered" @row-unhovered="rowUnhovered" :tbody-tr-class="rowClass" small hover sticky-header class="mt-3">
          <template #cell(data.created_at)="row">
            <span :id="'shippingmarker' + row.item.id">
              {{ row.item.data.index }}:
              {{ moment.utc(row.item.data.created_at).tz($tz).format('L LT') }}
            </span>
          </template>
          <template #cell(data.coordinates)="row">
            <b-button size="sm" @click.stop="openMapOn(row.item.data.lat + ',' + row.item.data.lng + '/@' + row.item.data.lat + ',' + row.item.data.lng + ',17z')" class="py-0 px-1" :title="$gettext('Quick view map on new window')">
              <font-awesome-icon :icon="['fa', 'map-marked-alt']" size="2x" style="font-size: 10px; vertical-align: middle;" />
            </b-button>
            <span class="text-nowrap">{{ row.item.data.lat }}, {{ row.item.data.lng }}</span>
          </template>
        </b-table>
      </div>
    </template>
  </leaflet-map>
</template>

<script>
import {
  TrackingService,
  ShippingService
} from '@/services/api'
import ShippingsMixin from '@/mixins/shippings.mixin'
import LeafletMap from '@/components/Maps/LeafletMap'

export default {
  name: 'ShippingMap',
  components: {
    LeafletMap
  },
  props: {
    shippingId: {
      type: String,
      default: ''
    },
    withTracking: {
      type: Boolean,
      default: false
    },
    popupType: {
      type: String,
      default: 'shipping'
    },
    updateEvery: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      shipping: {},
      markers: [],
      polylines: [],
      tracking: [],
      heathPath: [],
      showTracking: false,
      showHeathPath: false,
      showMarkersText: true,
      trackingWorking: false,
      fetchTimeout: null,
      trackingHistoryListFields: [
        {
          key: 'data.created_at',
          label: this.$gettext('Date'),
          sortable: true
        },
        {
          key: 'data.coordinates',
          label: this.$gettext('Info')
        }
      ],
      trackingHistoryListSortBy: 'data.created_at',
      trackingHistoryListSortDesc: true,
      showList: false,
      centerPoints: true
    }
  },
  watch: {
    markers(val) {
      setTimeout(function () {
        this.$refs.fullMap.addMarkers(val)
        this.$refs.fullMap.addPolylines(this.polylines)
      }.bind(this, val), 500)
    },
    tracking(val) {
      setTimeout(function () {
        this.$refs.fullMap.addTracking(val)
      }.bind(this, val), 500)
      if (val.length === 0) {
        this.$refs.fullMap.removeHeathPath()
      }
    },
    heathPath(val) {
      if (val) {
        this.$refs.fullMap.addHeathPath(this.heathPath)
      } else {
        this.$refs.fullMap.removeHeathPath()
      }
    },
    showTracking(val) {
      if (val) {
        const confirmedAt = this.shipping.status_history.find(x => x.status === 'CONFIRMED').created_at
        const deliveredAt = this.shipping.status_history.some(x => x.status === 'DELIVERED') ? this.shipping.status_history.find(x => x.status === 'DELIVERED').created_at : this.moment.utc(this.shipping.status_history[0].created_at).format('YYYY-MM-DD HH:mm:ss')
        const from = new Date(confirmedAt)
        const to = new Date(deliveredAt)
        let hours = (to.getTime() - from.getTime()) / 1000 / 60 / 60 / 24

        const trackingFetchesArray = []
        if (hours <= 1) {
          trackingFetchesArray.push(this.fetchTracking(confirmedAt, deliveredAt))
        } else {
          let i = 0
          while (hours > 0) {
            const toAdd = hours < 1 ? hours : 1
            trackingFetchesArray.push(this.fetchTracking(this.moment.utc(confirmedAt).add(i * 24, 'hours').format('YYYY-MM-DD HH:mm:ss'), this.moment.utc(confirmedAt).add((i + toAdd) * 24, 'hours').format('YYYY-MM-DD HH:mm:ss')))
            hours--
            i++
          }
        }
        Promise.all(trackingFetchesArray)
          .then(results => {
            let points = []
            for (const array of results) {
              points = points.concat(array)
            }
            this.tracking = this.buildTrackingMarkers(points)
          })
      } else {
        this.tracking = []
        this.showHeathPath = false
      }
    },
    showHeathPath(val) {
      if (val) {
        this.heathPath = this.tracking.map(x => {
          return [x.lat, x.lng, x.z]
        })
      } else {
        this.heathPath = []
      }
    }
  },
  mixins: [
    ShippingsMixin
  ],
  mounted() {
    this.fetchShipping()
  },
  beforeDestroy() {
    clearTimeout(this.fetchTimeout)
  },
  methods: {
    rowHovered(item, index, event) {
      this.$refs.fullMap.map.eachLayer((layer) => {
        if (layer.options.pane === 'tooltipPane') layer.removeFrom(this.$refs.fullMap.map)
      })
      item.icon.size = [20, 20]
      item.icon.anchor = [10, 10]
      this.$refs.fullMap.$refs['marker' + item.id][0].$refs.theMarker.mapObject.openTooltip()
      if (this.centerPoints) {
        this.$refs.fullMap.center = [item.lat, item.lng]
      }
      return item
    },
    rowUnhovered(item, index, event) {
      item.icon.size = [10, 10]
      item.icon.anchor = [5, 5]
      this.$nextTick(() => {
        this.$refs.fullMap.$refs['marker' + item.id][0].$refs.theMarker.mapObject.closeTooltip()
      })
      return item
    },
    markerOver(item, event) {
      this.$refs.fullMap.map.eachLayer((layer) => {
        if (layer.options.pane === 'tooltipPane') layer.removeFrom(this.$refs.fullMap.map)
      })
      this.tracking = this.tracking.map(x => {
        x.hover = false
        return x
      })
      const index = this.tracking.findIndex(x => x.id === item.id)
      if (index !== -1) {
        this.$set(this.tracking[index], 'hover', true)
        this.$set(this.tracking[index].icon, 'size', [20, 20])
        this.$set(this.tracking[index].icon, 'anchor', [10, 10])

        const table = document.getElementById('shipping-tracking-table')
        if (table) {
          table.parentElement.scrollTo({
            top: document.getElementById('shippingmarker' + item.id).parentElement.offsetTop,
            left: 0,
            behavior: 'smooth'
          })
        }
      }
    },
    markerOut(item, event) {
      const index = this.tracking.findIndex(x => x.id === item.id)
      if (index !== -1) {
        this.$set(this.tracking[index], 'hover', false)
        this.$set(this.tracking[index].icon, 'size', [10, 10])
        this.$set(this.tracking[index].icon, 'anchor', [5, 5])
      }
    },
    rowClass(item, type) {
      if (!item || type !== 'row') return
      if (item.hover === true) return 'hover'
    },
    async fetchTracking(dateFrom, dateTo) {
      this.trackingWorking = true
      const params = {
        page: 0,
        limit: 1,
        date_from: dateFrom,
        date_to: dateTo,
        filter_groups: [
          {
            filters: [
              ['shipping_id', 'eq', this.shipping.id]
            ]
          }
        ]
      }

      return await TrackingService.getAll({ params })
        .then(response => {
          this.trackingWorking = false

          return response.body.data.filter((thing, index, self) =>
            index === self.findIndex((t) => {
              return thing.lat === t.lat && thing.lng === t.lng
            })
          )
        })
    },
    buildTrackingMarkers(points) {
      const uniqueTrackingPointsByGeo = points
        .slice()
        .sort((a, b) => a.created_at.localeCompare(b.created_at))
        .filter((thing, index, self) =>
          index === self.findIndex((t) => {
            return thing.lat === t.lat && thing.lng === t.lng
          })
        )

      const markers = []
      let timeThreshold = null
      uniqueTrackingPointsByGeo.forEach((item, i) => {
        if (timeThreshold === null || Number.parseInt(item.created_at.replaceAll(/\D/g, '')) > timeThreshold) {
          markers.push({
            // 200000+ avoids ref collisions with the from/to (1/2), courier (1000) and item (100000) markers
            id: 200000 + i,
            lat: item.lat,
            lng: item.lng,
            icon: {
              type: 'marker-circle',
              size: [10, 10],
              anchor: [5, 5],
              color: '#000',
              text: (i + 1)
            },
            x: 11,
            y: 34,
            data: { ...item, index: (i + 1) },
            infoText: (i + 1) + ': ' + this.moment.utc(item.created_at).tz(this.$tz).format('L LT'),
            tooltip: true,
            z: uniqueTrackingPointsByGeo[(i + 1)] ? this.getSpeed(item, uniqueTrackingPointsByGeo[(i + 1)]) : 0
          })
          timeThreshold = Number.parseInt(item.created_at.replaceAll(/\D/g, '')) + 10
        }
      })
      return markers
    },
    fetchShipping() {
      if (this.updateEvery) {
        clearTimeout(this.fetchTimeout)
      }
      const params = {
        includes: [
          'courier.courier_available.tracking',
          'status_history',
          'items.item_tracking',
          'items.location',
          'items.last_known_location'
        ]
      }
      ShippingService.get(this.shippingId, { params })
        .then(response => {
          this.shipping = response.body
          this.setMapData()
          if (this.updateEvery) {
            this.fetchTimeout = setTimeout(() => {
              this.fetchShipping()
            }, this.updateEvery * 1000)
          }
        })
    },
    setMapData() {
      let icon = ''
      const markers = []

      const infoWindowHtml = this.formatShippingPopup(this.shipping)

      markers.push({
        id: 1,
        lat: this.shipping.address_from.lat || this.shipping.from_lat,
        lng: this.shipping.address_from.lng || this.shipping.from_lng,
        icon: {
          type: 'map-marker',
          size: [27, 36],
          anchor: [18, 39],
          color: '#999',
          text: '↑'
        },
        x: 11,
        y: 34,
        infoText: {
          id: this.shipping.id,
          type: 'shipping',
          html: infoWindowHtml,
          data: this.shipping,
          ids: [this.shipping.id]
        },
        popup: true
      })
      markers.push({
        id: 2,
        lat: this.shipping.address_to.lat || this.shipping.to_lat,
        lng: this.shipping.address_to.lng || this.shipping.to_lng,
        icon: {
          type: 'map-marker',
          size: [27, 36],
          anchor: [18, 39],
          color: '#999',
          text: '↓'
        },
        x: 11,
        y: 34,
        infoText: {
          id: this.shipping.id,
          type: 'shipping',
          html: infoWindowHtml,
          data: this.shipping,
          ids: [this.shipping.id]
        },
        popup: true
      })

      const data = {
        line: {
          polyLine: markers.filter(x => x.infoText.type !== 'courier'),
          polyLineOptions: {
            strokeColor: '#333',
            strokeWeight: 1
          }
        },
        markers
      }

      if (this.shipping.courier && this.shipping.courier.courier_available && this.shipping.courier.courier_available.tracking) {
        if (this.shipping.courier.courier_available.tracking[0]) {
          icon = this.$appConfig.staticBaseUrl + 'static/img/ic-map-' + this.vehicleTypes.find(x => x.id === this.shipping.courier.courier_available.vehicle_type_id).name.toLowerCase() + '-gray.svg'

          markers.push({
            id: 1000,
            lat: this.shipping.courier.courier_available.tracking[0].lat,
            lng: this.shipping.courier.courier_available.tracking[0].lng,
            icon,
            x: 25,
            y: 25,
            infoText: { id: this.shipping.courier.id, type: 'courier', user: this.shipping.courier },
            popup: true
          })
        }
      }

      if (this.shipping.items[0]?.item_tracking?.last_position_updated_at) {
        const item = this.shipping.items[0]
        let infoWindowHtml = ''
        infoWindowHtml += this.$options.filters.cleanentityprefix(item.reference_code)

        // Parse and reformat attributes
        if (item.attributes?.length) {
          item.parsedAttributes = {}
          for (const attribute of item.attributes) {
            item.parsedAttributes[attribute.name] = attribute.value
          }
        } else {
          item.parsedAttributes = null
        }

        let itemColor
        if (this.ecosystemModules.items.icon.colorByMinAge) {
          const daysIdle = this.elapsedTimeRaw(item.item_tracking.last_position_updated_at) / 86400000
          itemColor = this.itemColorByAge(daysIdle, 'items')
        }

        let direction = 0
        if (this.ecosystemModules.items.directionableBy) {
          direction = this.getItemDirection(this.ecosystemModules.items.directionableBy.split('.').reduce((o, i) => o[i], item))
        }

        let dynamic = null
        try {
          if (this.ecosystemModules.items.dynamicableBy) {
            dynamic = this.getItemDynamic(this.ecosystemModules.items.dynamicableBy.split('.').reduce((o, i) => o[i], item))
          }
        } catch (e) {
          console.log(e)
        }

        let load = null
        try {
          if (this.ecosystemModules.items.loadableBy) {
            load = this.ecosystemModules.items.loadableBy.split('.').reduce((o, i) => o[i], item)
          }
        } catch (e) {
          console.assert(e)
        }

        markers.push({
          id: 100000,
          cardType: 'item',
          lat: item[this.ecosystemModules.items.geolocalizableBy].lat || item.lat,
          lng: item[this.ecosystemModules.items.geolocalizableBy].lng || item.lng,
          icon: {
            type: this.ecosystemModules.items.icon.type,
            size: this.ecosystemModules.items.icon.size,
            anchor: this.ecosystemModules.items.icon.anchor,
            popupAnchor: this.ecosystemModules.items.icon.popupAnchor,
            color: itemColor,
            text: this.ecosystemModules.items.icon.text || this.$options.filters.cleanentityprefix(item.reference_code),
            direction,
            dynamic,
            load
          },
          x: 30,
          y: 30,
          optimized: false,
          infoText: {
            id: item.id,
            type: 'item-id',
            html: infoWindowHtml,
            color: itemColor,
            data: item,
            isShared: item.isShared || false
          },
          popup: true
        })
      }

      this.markers = markers
      this.polylines = [data.line]
    }
  }
}
</script>
